import { useEffect, useMemo, useState } from 'react'
import { debounce } from 'lodash'
import {
  SelectDataHandleIProps,
  SelectLoadType,
  UseHandleSelectDataReturnType,
} from './select-types'
import { MetadataType } from '../../../../../constants/metadata.types'
import commonRequestHandler from '../../../../../utils/common-request-handler'
import uniqueArrayById from '../../../../../utils/unique-array-by-id'

// ? Hook để xử lý data cho search Select
const useHandleSelectData = <T extends { id: string }>({
  mode, // multiple hay single
  getListDataFunction, // hàm fetch data
  includeItems, // render item đã được chọn trước đó
  includeIds, // fetch thêm item đã được chọn trước đó
  fetchOnFocus, // fetch khi focus vào select
  noInitFetch, // Chặn tự động fetch
  noPaging,
}: SelectDataHandleIProps<T>): UseHandleSelectDataReturnType<T> => {
  const [dataList, setDataList] = useState<T[] | undefined>()
  const [dataMetadata, setDataMetadata] = useState<MetadataType>()
  const [searchItems, setSearchItems] = useState<T[]>()
  const [searchMetadata, setSearchMetadata] = useState<MetadataType>()
  const [loadType, setLoadType] = useState<SelectLoadType>()
  const [selectData, setSelectData] = useState<T[]>([])

  const handleResetAll = () => {
    setDataList(undefined)
    setDataMetadata(undefined)
    setSearchItems(undefined)
    setSearchMetadata(undefined)
    setLoadType(undefined)
    setSelectData([])
  }

  // Check xem đã kéo data lần đầu chưa
  const initFetch = !!dataList

  // Convert includeIds theo "mode"
  const getIncludeIds = () => {
    if (!includeIds) {
      return undefined
    }
    if (mode === 'multiple') {
      // ? Giới hạn includeIds ít hơn 10
      if (includeIds.length >= 10) {
        return includeIds.filter((_, index) => index <= 8).join(',')
      }
      return includeIds.join(',')
    }

    return includeIds
  }

  // Kéo data
  const getInitData = async () => {
    setLoadType('fetch')
    if (noPaging) {
      const res = await getListDataFunction({
        filter: { includeIds: getIncludeIds() },
      })
      setLoadType(undefined)
      commonRequestHandler({
        status: res?.status,
        onSuccess: () => res && setDataList(res.data),
      })
    } else {
      const res = await getListDataFunction({
        filter: { includeIds: getIncludeIds() },
      })
      setLoadType(undefined)
      if (!res?.data) return
      commonRequestHandler({
        status: res?.status,
        onSuccess: () => {
          setDataList(res.data.data)
          setDataMetadata(res.data.metadata)
        },
      })
    }
  }

  // Load more data
  const loadMoreData = async () => {
    if (noPaging) {
      return
    }
    const nextPageId = searchItems
      ? searchMetadata?.paging?.cursor?.next
      : dataMetadata?.paging?.cursor?.next

    if (!nextPageId) return

    setLoadType('loadmore')
    const res = await getListDataFunction({
      paging: { pageId: nextPageId },
      filter: { includeIds: getIncludeIds() },
    })
    setLoadType(undefined)
    if (!res?.data) return
    commonRequestHandler({
      status: res?.status,
      onSuccess: () => {
        if (searchItems) {
          setSearchItems((prev) => [...(prev || []), ...res.data.data])
          setSearchMetadata(res.data.metadata)
          return
        }
        setDataList((prev) => [...(prev || []), ...res.data.data])
        setDataMetadata(res.data.metadata)
      },
    })
  }

  // Search data
  const getSearchData = async (name: string | undefined) => {
    setLoadType('search')
    if (noPaging) {
      const res = await getListDataFunction({
        filter: { name },
      })
      setLoadType(undefined)
      if (!res) return
      commonRequestHandler({
        status: res?.status,
        onSuccess: () => {
          setSearchItems(res.data)
        },
      })
    } else {
      const res = await getListDataFunction({
        filter: { name },
      })
      setLoadType(undefined)
      if (!res?.data) return
      commonRequestHandler({
        status: res?.status,
        onSuccess: () => {
          setSearchItems(res.data.data)
          setSearchMetadata(res.data.metadata)
        },
      })
    }
  }

  const debounceSearchData = debounce(
    (name: string | undefined) => {
      // Xóa kết quả khi delete hết field search
      if (!name) {
        setSearchItems(undefined)
        return
      }

      getSearchData(name)
    },
    500,
    { maxWait: 800, leading: false, trailing: true }
  )

  const searchData = (name: string | undefined) => {
    debounceSearchData(name)
  }
  // // _____________________________________________________

  // Kéo data lần đầu
  useEffect(() => {
    // * Check điều kiện chặn fetch lần đầu
    if (fetchOnFocus || noInitFetch) {
      return
    }
    getInitData()
  }, [])

  // Xác định luồng data chính, ưu tiên hiển thị các items đang search
  const data = searchItems || dataList || []

  // Xác định các includeItems chưa có trong luồng data chính
  const notIncludedData = (() => {
    if (!includeItems) {
      return []
    }
    if (mode) {
      return includeItems.filter((x) => !data.map((y) => y.id).includes(x.id))
    }
    if (data.map((x) => x.id).includes(includeItems.id)) {
      return []
    }

    return [includeItems]
  })()

  // Data hiển thị
  const viewData: T[] = useMemo(
    () => uniqueArrayById([...data, ...notIncludedData, ...selectData]),
    [data, notIncludedData, selectData]
  )

  // xác định metadata hiện tại
  const metaData = searchItems ? searchMetadata : dataMetadata

  return {
    viewData,
    dataList,
    setDataList,
    searchItems,
    setSearchItems,
    metaData,
    loadType,
    initFetch,
    loadMoreData,
    searchData,
    getInitData,
    fetchOnFocus: fetchOnFocus || false,
    handleResetAll,
    setSelectData,
  }
}

export default useHandleSelectData
